/**
 * ============================================================================
 * Purchase 模块 - 常量定义
 * ============================================================================
 * 
 * @description
 * 本文件定义 Purchase 模块的常量映射，包括枚举标签、状态文本等。
 * 
 * @module Types/Purchase/Constants
 * 
 * @backend-mapping
 * - Model常量 → backend/app/Modules/Purchase/Models/*.php (常量定义)
 * - Resource属性方法 → backend/app/Modules/Purchase/Resources/*.php (get*TextAttribute)
 * 
 * @note
 * - 常量值必须与后端完全一致
 * - 标签文本必须与后端完全一致
 * - 使用 Record<number, string> 或 Record<string, string> 格式
 * 
 * @author YSFL Team
 * @last-updated 2025-11-03
 * ============================================================================
 */

import {
  SupplierType,
  CompanyType,
  QualityLevel,
  PurchaseOrderStatus,
  Priority,
  OrderSourceType,
  QualityStatus,
  SupplierStatus,
  PurchaseReturnType,
  PurchaseReturnStatus,
} from './enums';

/**
 * 供应商类型标签映射
 * @backend Supplier::getSupplierTypeTextAttribute()
 */
export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  [SupplierType.RAW_MATERIAL]: '原材料',
  [SupplierType.SEMI_FINISHED]: '半成品',
  [SupplierType.MRO]: 'MRO',
  [SupplierType.SERVICE]: '服务',
  [SupplierType.OUTSOURCE]: '委外加工',
};

/**
 * 企业类型标签映射
 * @backend Supplier::COMPANY_*
 */
export const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  [CompanyType.STATE_OWNED]: '国企',
  [CompanyType.PRIVATE]: '民企',
  [CompanyType.FOREIGN]: '外企',
  [CompanyType.INDIVIDUAL]: '个体',
};

/**
 * 质量等级标签映射
 * @backend Supplier::getQualityLevelTextAttribute()
 */
export const QUALITY_LEVEL_LABELS: Record<QualityLevel, string> = {
  [QualityLevel.A]: 'A级',
  [QualityLevel.B]: 'B级',
  [QualityLevel.C]: 'C级',
  [QualityLevel.D]: 'D级',
};

/**
 * 采购订单状态标签映射
 * @backend PurchaseOrder::getStatusTextAttribute()
 */
export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  [PurchaseOrderStatus.PENDING]: '待审核',
  [PurchaseOrderStatus.CONFIRMED]: '已确认',
  [PurchaseOrderStatus.PARTIALLY_RECEIVED]: '部分到货',
  [PurchaseOrderStatus.FULLY_RECEIVED]: '全部到货',
  [PurchaseOrderStatus.CLOSED]: '已关闭',
  [PurchaseOrderStatus.CANCELLED]: '已取消',
};

/**
 * 优先级标签映射
 * @backend PurchaseOrder::getPriorityTextAttribute()
 */
export const PRIORITY_LABELS: Record<Priority, string> = {
  [Priority.URGENT]: '紧急',
  [Priority.HIGH]: '高',
  [Priority.MEDIUM]: '中',
  [Priority.LOW]: '低',
};

/**
 * 订单来源类型标签映射
 * @backend PurchaseOrder::SOURCE_*
 */
export const ORDER_SOURCE_TYPE_LABELS: Record<OrderSourceType, string> = {
  [OrderSourceType.MANUAL]: '手工创建',
  [OrderSourceType.MRP]: 'MRP生成',
  [OrderSourceType.SUGGESTION]: '需求建议',
};

/**
 * 质检状态标签映射
 * @backend PurchaseReceiving::getQualityStatusTextAttribute()
 */
export const QUALITY_STATUS_LABELS: Record<QualityStatus, string> = {
  [QualityStatus.PENDING]: '待检',
  [QualityStatus.QUALIFIED]: '合格',
  [QualityStatus.REJECTED]: '不合格',
};

/**
 * 供应商状态标签映射
 * @backend Supplier::STATUS_*
 */
export const SUPPLIER_STATUS_LABELS: Record<SupplierStatus, string> = {
  [SupplierStatus.ACTIVE]: '启用',
  [SupplierStatus.INACTIVE]: '停用',
};

/**
 * 获取供应商类型文本
 */
export function getSupplierTypeLabel(type: SupplierType): string {
  return SUPPLIER_TYPE_LABELS[type] || '未知';
}

/**
 * 获取质量等级文本
 */
export function getQualityLevelLabel(level: QualityLevel): string {
  return QUALITY_LEVEL_LABELS[level] || '未评级';
}

/**
 * 获取采购订单状态文本
 */
export function getPurchaseOrderStatusLabel(status: PurchaseOrderStatus): string {
  return PURCHASE_ORDER_STATUS_LABELS[status] || '未知';
}

/**
 * 获取优先级文本
 */
export function getPriorityLabel(priority: Priority): string {
  return PRIORITY_LABELS[priority] || '未知';
}

/**
 * 获取质检状态文本
 */
export function getQualityStatusLabel(status: QualityStatus): string {
  return QUALITY_STATUS_LABELS[status] || '未知';
}

// ============================================================================
// 采购退货相关常量
// ============================================================================

/**
 * 采购退货类型标签映射
 * @backend PurchaseReturn::getReturnTypes()
 */
export const PURCHASE_RETURN_TYPE_LABELS: Record<PurchaseReturnType, string> = {
  [PurchaseReturnType.QUALITY_ISSUE]: '质量问题',
  [PurchaseReturnType.WRONG_DELIVERY]: '发错货',
  [PurchaseReturnType.OVER_DELIVERY]: '多发货',
  [PurchaseReturnType.DAMAGED]: '运输损坏',
  [PurchaseReturnType.OTHER]: '其他',
};

/**
 * 采购退货状态标签映射
 * @backend PurchaseReturn::getStatuses()
 */
export const PURCHASE_RETURN_STATUS_LABELS: Record<PurchaseReturnStatus, string> = {
  [PurchaseReturnStatus.DRAFT]: '草稿',
  [PurchaseReturnStatus.PENDING]: '待审批',
  [PurchaseReturnStatus.APPROVED]: '已审批',
  [PurchaseReturnStatus.SHIPPING]: '退货中',
  [PurchaseReturnStatus.COMPLETED]: '已完成',
  [PurchaseReturnStatus.REJECTED]: '已驳回',
  [PurchaseReturnStatus.CANCELLED]: '已取消',
};

/**
 * 获取采购退货类型文本
 */
export function getPurchaseReturnTypeLabel(type: PurchaseReturnType): string {
  return PURCHASE_RETURN_TYPE_LABELS[type] || '未知';
}

/**
 * 获取采购退货状态文本
 */
export function getPurchaseReturnStatusLabel(status: PurchaseReturnStatus): string {
  return PURCHASE_RETURN_STATUS_LABELS[status] || '未知';
}
